import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import { ContainerGrid, ContainerGridItem } from '@/components/UI/ContainerGrid/ContainerGrid';
import InputText from '@/components/UI/InputText/InputText';
import LabelDisabledByPDRS from '@/components/UI/LabelDisabledByPDRS/LabelDisabledByPDRS';
import MandatoryLabel from '@/components/UI/MandatoryLabel/MandatoryLabel';
import { DESCRIPTION, descriptionIssues, HELP_AREA, highlightedFieldsInitialState } from '@/helpers/constants';
import { isFieldDisabledBYPRRD } from '@/helpers/utils';
import { useFieldSelection } from '@/hooks/useFieldSelection';
import {
  getCampaignChanges,
  getCampaignInfo,
  getCampaignDescription,
  getCampaignValidationsActive,
  getDescriptionValidations,
  getOriginalValues,
} from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import { CampaignInfo } from '@/types';
import { Description as DescriptionType } from '@/types/Description';
import styles from './Description.module.scss';
import { errorMessage, labelFormat, maxLengthError } from './Description.utils';

interface ProblemsSectionProps {
  description: DescriptionType;
  headerLanguage: string;
  checkDisabled: boolean;
  updateState: (newState: DescriptionType) => void;
  tabSelected: string;
  setTabSelected: (tab: string) => void;
  descValidations: any;
  activeShowMinError: any;
  validateField: any;
  campaignChanges: any;
  campaignInfo: CampaignInfo;
  setHelpArea: (help: string) => void;
  onBlur: (tab: string, value: string) => void;
}

const ProblemsSection: React.FC<ProblemsSectionProps> = (props) => {
  const {
    description,
    headerLanguage,
    checkDisabled,
    updateState,
    tabSelected,
    setTabSelected,
    descValidations,
    activeShowMinError,
    validateField,
    campaignChanges,
    campaignInfo,
    setHelpArea,
    onBlur,
  } = props;

  const { t } = useTranslation();
  const { highlightedFields = highlightedFieldsInitialState, isCreatorView } = useFieldSelection({ tab: DESCRIPTION.PROBLEMS, fieldName: '' });
  const { selectedFields, isSelectionMode } = highlightedFields;
  const { active: showChangesActive } = campaignChanges || {};
  const didMountRef = useRef(false);

  const isFieldHighlightedByReviewer = (option: string) => (isCreatorView || isSelectionMode) && selectedFields?.['DESCRIPTION']?.includes(option);

  const getExtraClasses = (option: string) => {
    const selected = tabSelected === option ? styles.TabSelected : '';
    const errorValidation = descValidations?.[headerLanguage]?.problems?.[option]?.invalid
      ? 'error'
      : '';
    const errorChanges = showChangesActive
      && campaignChanges?.description?.find(
        (f: string) => f === `problems.${option}.${headerLanguage}`,
      );
    const error = errorValidation || errorChanges ? 'error' : '';

    return `${selected} ${error} ${isFieldHighlightedByReviewer(option) ? 'error' : ''}`;
  };

  const handleChangeProblems = (value: string) => {
    activeShowMinError(DESCRIPTION.PROBLEMS, value);
    validateField(DESCRIPTION.PROBLEMS, value);
    const problemSelected = {
      ...description.problems[tabSelected],
      [headerLanguage]: value,
    };
    const newState = {
      ...description,
      problems: {
        ...description.problems,
        [tabSelected]: problemSelected,
      },
    };
    updateState(newState);
  };

  useEffect(() => {
    const focusInputAtEnd = (elementId: string) => {
      const inputToBeFocused = document.getElementById(elementId) as HTMLInputElement;
      const firstTimeFocus = !didMountRef.current;
      if (!inputToBeFocused) return;

      inputToBeFocused.focus({ preventScroll: firstTimeFocus });
      const textLength = inputToBeFocused.value?.length;
      inputToBeFocused.setSelectionRange(textLength, textLength);

      if (!didMountRef.current) {
        didMountRef.current = true;
      }
    };
    if (!tabSelected) return;

    focusInputAtEnd(`technical-problem-description-${tabSelected}`);
  }, [tabSelected]);

  return (
    <AccordionPanel
      title={t('description.ocurredIssue')}
      handleClick={() => setHelpArea(HELP_AREA.PROBLEM)}
      invalid={descriptionIssues.some(
        (issue) => descValidations?.[headerLanguage]?.problems?.[issue]?.invalid,
      )}
    >
      <div className={styles.Tabs}>
        <ContainerGrid columns={{ xs: 1, sm: 5, md: 5, lg: 5 }} gap={0}>
          <ContainerGridItem span={{ xs: 1, sm: 2, md: 1, lg: 1 }}>
            <div className={`${styles.TabsLeft} w-100`}>
              <ul id="occurred-issues">
                {descriptionIssues.map((option) => (
                  <li
                    id={`tab-${option}`}
                    key={option}
                    className={`${styles.Tab} ${getExtraClasses(option)}`}
                    onClick={() => {
                      setTabSelected(option);
                    }}
                  >
                    <LabelDisabledByPDRS campaignInfo={campaignInfo} label={t(`description.${option}`)} fieldName={`description.problems.${option}`} />
                    <MandatoryLabel />
                  </li>
                ))}
              </ul>
            </div>
          </ContainerGridItem>

          <ContainerGridItem span={{ xs: 1, sm: 3, md: 4, lg: 4 }}>
            <div className={`${styles.TabsRight} w-100`}>
              {descriptionIssues.map(
                (tab) => tabSelected === tab && (
                  <InputText
                    key={tab}
                    id={`technical-problem-description-${tab}`}
                    disabled={checkDisabled || isFieldDisabledBYPRRD(campaignInfo, `description.problems.${tabSelected}`)}
                    fieldPath={[DESCRIPTION.PROBLEMS, tabSelected]}
                    multiline
                    rows={9}
                    maxLength={512}
                    label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label={t(`description.${tabSelected}`)} fieldName={`description.problems.${tabSelected}`} />}
                    handleChange={(ev) => handleChangeProblems(ev.target.value)}
                    value={description.problems[tabSelected]?.[headerLanguage] || ''}
                    placeholder={t(`description.placeholderProblemDescription.${tabSelected}`) || ''}
                    onBlur={(ev) => onBlur(labelFormat(tab), ev.target.value)}
                    helperText={[
                      ...(!checkDisabled
                        && errorMessage(
                          descValidations?.[headerLanguage]?.problems?.[
                            tabSelected
                          ],
                          512,
                          t,
                        )
                        ? [
                          errorMessage(
                            descValidations?.[headerLanguage]?.problems?.[
                              tabSelected
                            ],
                            512,
                            t,
                          ),
                        ]
                        : []),
                      ...(!checkDisabled
                        && maxLengthError(
                          description.problems[tabSelected][headerLanguage],
                          512,
                          t,
                        )
                        ? [
                          maxLengthError(
                            description.problems[tabSelected][headerLanguage],
                            512,
                            t,
                          ),
                        ]
                        : []),
                    ]}
                  />
                ),
              )}
            </div>
          </ContainerGridItem>
        </ContainerGrid>
      </div>
    </AccordionPanel>
  );
};

const mapStateToProps = (state) => ({
  description: getCampaignDescription(state),
  originalValues: getOriginalValues(state),
  headerLanguage:
    getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  descValidations: getDescriptionValidations(state),
  activeValidations: getCampaignValidationsActive(state),
  campaignChanges: getCampaignChanges(state),
  campaignInfo: getCampaignInfo(state),
});

const actionCreators = {
  setHelpArea: setAreaHelp,
};

export default connect(mapStateToProps, actionCreators)(ProblemsSection);
